import { useEffect, useRef } from 'react'

import { getChannelClient } from 'src/utils/messages/singleton'

// Lock/unlock the embed host's donation <dialog> against closing while a
// submission is in flight, so the donor can't dismiss it mid-payment.
// `locked` is the widget's in-flight signal — v7 passes its single
// App-level `submitInFlight`; v6 has no single choke point, so each
// payment panel passes its own (fiat/deferred `record.processing`,
// wallet `loading`). Sends `lockModal` when it goes true and
// `unlockModal` when it goes false; the host (embed/Widgets.js) resolves
// the dialog from the sending iframe and gates closeDialog. See
// embed/dialog.js lockDialog/unlockDialog + its safety timer.
//
// Only meaningful when embedded in a host dialog: skipped in top-frame
// mode (no channel host to hop to), and a no-op host-side for inline
// (non-modal) embeds (the iframe has no `.plg-dialog` ancestor). The ref
// skips the initial render so a mounted-but-idle panel doesn't emit a
// spurious unlock on mount — v6 mounts every payment panel at once, so
// several would otherwise fire on load; only real transitions send.
const useModalLock = locked => {
  const previous = useRef(locked)
  // Latest `locked`, updated every render so the unmount cleanup below
  // reads the current value (not a stale closure).
  const lockedRef = useRef(locked)
  lockedRef.current = locked

  useEffect(() => {
    if (locked === previous.current) return
    previous.current = locked
    if (parent === self) return
    getChannelClient().send(locked ? 'lockModal' : 'unlockModal')
  }, [ locked ])

  // Safety net for the rare case where the widget unmounts mid-lock (its
  // React tree is torn down before the submit settles) — release so the
  // host dialog isn't stuck until the safety timer. Empty deps so this
  // runs ONLY on real unmount; putting the release in the [locked]
  // effect's cleanup would instead fire on every transition.
  useEffect(() => () => {
    if (lockedRef.current && parent !== self)
      getChannelClient().send('unlockModal')
  }, [])
}

export default useModalLock
